import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  clampFieldToPage,
  fieldToScreenRect,
  screenDeltaToPageDelta,
  screenPointToPage,
  type Point,
  type RectLike,
} from './coords';
import type { ViewerLayoutState } from './usePdfLayout';
import { FIELD_TYPE_LABEL, type FieldType, type OverlayField } from './fieldTypes';

export type { OverlayField };

interface Props {
  layout: ViewerLayoutState;
  fields: OverlayField[];
  signerColor: (signerId: string) => string;
  selectedFieldId: string | null;
  onSelect?: (fieldId: string | null) => void;
  onMove?: (fieldId: string, x: number, y: number, page: number) => void;
  onResize?: (fieldId: string, w: number, h: number) => void;
  onDelete?: (fieldId: string) => void;
}

type DragMode =
  | { kind: 'move'; fieldId: string; startClient: Point; origin: { x: number; y: number; page: number } }
  | { kind: 'resize'; fieldId: string; corner: 'se'; startClient: Point; size: { w: number; h: number } }
  | null;

const TYPE_ICON: Record<FieldType, string> = {
  signature: '✍',
  initials: '✒',
  date: '📅',
  name: '🅰',
};

export default function FieldOverlay({
  layout,
  fields,
  signerColor,
  selectedFieldId,
  onSelect,
  onMove,
  onResize,
  onDelete,
}: Props) {
  const [drag, setDrag] = useState<DragMode>(null);
  const dragRef = useRef<DragMode>(null);
  dragRef.current = drag;
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  useEffect(() => {
    if (!drag) return;
    const handleMove = (event: PointerEvent) => {
      const current = dragRef.current;
      if (!current) return;
      const dx = event.clientX - current.startClient.x;
      const dy = event.clientY - current.startClient.y;
      const field = fieldsRef.current.find((f) => f.id === current.fieldId);
      if (!field) return;
      if (current.kind === 'move' && onMoveRef.current) {
        const delta = screenDeltaToPageDelta(layout.scale, dx, dy);
        const pageDim = layout.pages[field.page];
        const next = clampFieldToPage(
          { x: 0, y: 0, w: pageDim?.w ?? field.w, h: pageDim?.h ?? field.h },
          { x: current.origin.x + delta.x, y: current.origin.y + delta.y, w: field.w, h: field.h }
        );
        onMoveRef.current(current.fieldId, next.x, next.y, field.page);
      } else if (current.kind === 'resize' && onResizeRef.current) {
        const delta = screenDeltaToPageDelta(layout.scale, dx, dy);
        const pageDim = layout.pages[field.page];
        const maxW = (pageDim?.w ?? 1000) - field.x;
        const maxH = (pageDim?.h ?? 1000) - field.y;
        onResizeRef.current(
          current.fieldId,
          Math.max(20, Math.min(current.size.w + delta.x, maxW)),
          Math.max(14, Math.min(current.size.h + delta.y, maxH))
        );
      }
    };
    const handleUp = () => setDrag(null);
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [drag, layout.scale, layout.pages]);

  const byPage = useMemo(() => {
    const map = new Map<number, OverlayField[]>();
    fields.forEach((f) => {
      const list = map.get(f.page) ?? [];
      list.push(f);
      map.set(f.page, list);
    });
    return map;
  }, [fields]);

  if (!layout.ready) return null;

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none', overflow: 'hidden' }}>
      {[...byPage.entries()].map(([page, pageFields]) => {
        const pageOriginRect: RectLike | undefined = layout.pages[page];
        if (!pageOriginRect) return null;
        return pageFields.map((field) => {
          const rect = fieldToScreenRect(pageOriginRect, layout.scale, layout.scroll, layout.offset, field);
          const color = signerColor(field.signerId);
          const selected = selectedFieldId === field.id;
          return (
            <div
              key={field.id}
              style={{
                position: 'absolute',
                left: rect.left,
                top: rect.top,
                width: rect.width,
                height: rect.height,
                pointerEvents: 'auto',
                cursor: drag?.kind === 'move' || !drag ? 'move' : 'nwse-resize',
                borderRadius: 4,
                border: `2px ${selected ? 'solid' : 'dashed'} ${color}`,
                background: `${color}22`,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                userSelect: 'none',
                zIndex: selected ? 30 : 20,
                transition: drag ? 'none' : 'left 0.06s linear, top 0.06s linear',
              }}
              onPointerDown={(event) => {
                event.stopPropagation();
                onSelect?.(field.id);
                setDrag({
                  kind: 'move',
                  fieldId: field.id,
                  startClient: { x: event.clientX, y: event.clientY },
                  origin: { x: field.x, y: field.y, page: field.page },
                });
              }}
            >
              <span
                style={{
                  color,
                  fontWeight: 600,
                  fontSize: Math.max(9, Math.min(14, rect.height * 0.24)),
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '96%',
                }}
              >
                {TYPE_ICON[field.type]} {field.label || FIELD_TYPE_LABEL[field.type]}
              </span>
              {selected ? (
                <>
                  <div
                    title="Resize"
                    style={{
                      position: 'absolute',
                      right: -7,
                      bottom: -7,
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      background: color,
                      border: '2px solid #0a1622',
                      cursor: 'nwse-resize',
                    }}
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      setDrag({
                        kind: 'resize',
                        fieldId: field.id,
                        corner: 'se',
                        startClient: { x: event.clientX, y: event.clientY },
                        size: { w: field.w, h: field.h },
                      });
                    }}
                  />
                  {onDelete ? (
                    <div
                      title="Delete field"
                      style={{
                        position: 'absolute',
                        right: -7,
                        top: -7,
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        background: '#d16a6a',
                        color: '#fff',
                        fontSize: 10,
                        lineHeight: '12px',
                        textAlign: 'center',
                        border: '2px solid #0a1622',
                        cursor: 'pointer',
                        fontFamily: 'monospace',
                      }}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        onDelete(field.id);
                      }}
                    >
                      ×
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          );
        });
      })}
    </div>
  );
}

export function pagePointFromContainer(
  layout: ViewerLayoutState,
  container: HTMLElement,
  clientX: number,
  clientY: number
): { page: number; x: number; y: number } | null {
  const rect = container.getBoundingClientRect();
  return screenPointToPage(layout.pages, layout.scale, layout.scroll, layout.offset, {
    x: clientX - rect.left,
    y: clientY - rect.top,
  });
}
